// Income & pay data layer (finance table, per-user only — see store.js).
//
// Row families:
//   • employment#<uuid>                      — an employer + dates + pay schedule.
//                                              Carries the recurring BONUS scheme:
//                                              payout day, super flag, actuals by FY.
//   • comp-event#<employmentUuid>:<uuid>     — a dated compensation event:
//       review   — a pay review effective from `date`. Every part is optional
//                  and an unset part (null) CARRIES FORWARD from earlier reviews:
//                  annual base (EXCLUDING super), super (SG or a rate), salary
//                  sacrifice, bonus target (% of base or an amount), and a new
//                  grant — cash-in-lieu of RSUs: a USD total paid in equal
//                  tranches every `intervalMonths` from its first vest — and/or
//                  a new SHARE grant (RSUs: a share count of a ticker on the
//                  same kind of schedule; vests + sales live in shares.js);
//       one-off  — e.g. a signing bonus; optionally spread evenly over
//                  `payPeriods` pays (each instalment taxed as ordinary pay).
//       leave    — a period of UNPAID leave, `date` → `endDate` (inclusive):
//                  no base pay for those days (so no super on it, and the
//                  bonus projection excludes them). `vestingDelayPeriods`
//                  (optional) moves every grant tranche vesting on/after the
//                  leave start later by that many vesting intervals.
//   • income-settings#                       — one row: tax profile, private
//                                              hospital cover and per-FY figures.
// A scenario (what-if) employment carries `scenario: true` and is excluded
// from actual figures unless explicitly included.
//
// Every stored money value is `{ amount, currency }` (money.js `readMoney`;
// amount = minor units, or a decimal for a per-share price). Salary, salary
// sacrifice, bonus targets and the per-FY figures accept only AUD for now
// (`AUD_ONLY`); grant totals, tranche actuals, actual bonuses, one-offs and
// share prices accept any supported currency. Derived values
// (`resolveReviews`, `fyAmounts`) are plain AUD cents. Rates are decimals
// (0.12 = 12%).

import { financeStore } from './store.js';
import { AUD_ONLY, money, readMoney } from './money.js';
import { isoDate } from './fy.js';
import { normaliseSchedule } from './pay-schedule.js';

export const EMPLOYMENT_PREFIX = 'employment#';
export const COMP_EVENT_PREFIX = 'comp-event#';
export const INCOME_SETTINGS_ID = 'income-settings#';
export const COMP_EVENT_KINDS = Object.freeze(['review', 'one-off', 'leave']);
/** Default bonus payout day ('MM-DD'). */
export const DEFAULT_BONUS_PAYOUT = '09-30';
export const MEDICARE_EXEMPTIONS = Object.freeze(['none', 'half', 'full']);

function randomUuid() {
  return crypto.randomUUID();
}

// ─── Normalisation (pure) ────────────────────────────────────────────────────

function dateOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  try {
    return isoDate(v);
  } catch {
    return null;
  }
}

function rateOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function strOrNull(v) {
  const s = String(v ?? '').trim();
  return s === '' ? null : s;
}

/** An employment's stored shape. Dates are 'YYYY-MM-DD'; `endDate` null = ongoing. */
export function normaliseEmployment(e) {
  const startDate = dateOrNull(e?.startDate) ?? isoDate(new Date());
  let endDate = dateOrNull(e?.endDate);
  if (endDate && endDate < startDate) endDate = startDate;
  return {
    employer: strOrNull(e?.employer) ?? 'Employer',
    startDate,
    endDate,
    pay: normaliseSchedule(e?.pay, startDate),
    // A second job usually does NOT claim the tax-free threshold.
    taxFreeThreshold: e?.taxFreeThreshold !== false,
    scenario: e?.scenario === true,
    notes: strOrNull(e?.notes),
    bonus: normaliseBonusScheme(e?.bonus),
  };
}

/** 'MM-DD' (a valid day of a leap year), else the default payout day. */
function payoutOrDefault(v) {
  const m = /^(\d{2})-(\d{2})$/.exec(String(v ?? ''));
  if (!m) return DEFAULT_BONUS_PAYOUT;
  const month = Number(m[1]);
  const day = Number(m[2]);
  const max = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  return max && day >= 1 && day <= max ? m[0] : DEFAULT_BONUS_PAYOUT;
}

/** The employment's recurring bonus scheme (targets come from pay reviews). */
function normaliseBonusScheme(b) {
  const actualByFy = {};
  for (const [k, v] of Object.entries(b?.actualByFy ?? {})) {
    const actual = readMoney(v, { min: 0 });
    if (/^\d{4}$/.test(k) && actual) actualByFy[k] = actual;
  }
  return {
    // true / false = set in the editor; null = a row saved before the flag
    // existed (see `bonusOffered`, which infers it from the reviews).
    offered: typeof b?.offered === 'boolean' ? b.offered : null,
    payout: payoutOrDefault(b?.payout),
    superApplies: b?.superApplies !== false,
    actualByFy,
  };
}

/**
 * Whether an employment offers a bonus. An unset flag (older rows) counts as
 * offered when any entered actual or pay-review target says so.
 * @param {object} employment - normalised.
 * @param {Array<object>} events - its comp events (normalised).
 */
export function bonusOffered(employment, events = []) {
  const { offered, actualByFy } = employment.bonus;
  if (offered !== null) return offered;
  if (Object.keys(actualByFy).length) return true;
  return events.some((e) => e.kind === 'review' && bonusTargetActive(e.bonusTarget));
}

/**
 * A percent-or-amount part's number: the rate for `kind:'percent'`, the AUD
 * cents for `kind:'amount'` (its `value` is a money value).
 */
export function partValue(part) {
  if (!part) return 0;
  return part.kind === 'amount' ? (part.value?.amount ?? 0) : (part.value ?? 0);
}

/** Whether a (stored or resolved) bonus target pays anything. */
export function bonusTargetActive(t) {
  return Boolean(t) && partValue(t) > 0;
}

/** A percent-or-amount part's stored value (amounts are AUD money values). */
function partOrNull(kind, value) {
  if (kind === 'percent') return rateOrNull(value);
  return readMoney(value, { currencies: AUD_ONLY, min: 0 });
}

/** A salary sacrifice: null = unchanged (review), `{kind:'none'}` = stop. */
function normaliseSacrifice(s) {
  if (!s) return null;
  if (s.kind === 'none') return { kind: 'none' };
  if (s.kind !== 'percent' && s.kind !== 'amount') return null;
  const value = partOrNull(s.kind, s.value);
  return value && partValue({ kind: s.kind, value }) > 0
    ? { kind: s.kind, value }
    : { kind: 'none' };
}

/** Super: null = unchanged, `{mode:'sg'}` = the statutory rate, else a fixed rate. */
function normaliseSuper(s) {
  if (!s) return null;
  if (s.mode === 'sg') return { mode: 'sg' };
  const rate = rateOrNull(s.rate);
  return s.mode === 'rate' && rate !== null ? { mode: 'rate', rate } : null;
}

/** Bonus target: null = unchanged; value 0 = no bonus from this review on. */
function normaliseBonusTarget(t) {
  if (!t || (t.kind !== 'percent' && t.kind !== 'amount')) return null;
  const value = partOrNull(t.kind, t.value);
  return value === null ? null : { kind: t.kind, value };
}

/**
 * A new grant awarded at a review (first vest defaults to the review date):
 * a `total` (money value) paid in equal tranches; `actualByTranche` (index →
 * money value) = the amount actually paid, entered once known.
 */
function normaliseGrant(g, reviewDate) {
  if (!g) return null;
  const total = readMoney(g.total, { min: 0 });
  if (!total?.amount) return null;
  const tranches = Math.max(1, Math.round(Number(g.tranches) || 16));
  const actualByTranche = {};
  for (const [k, v] of Object.entries(g.actualByTranche ?? {})) {
    const i = Number(k);
    const actual = readMoney(v);
    if (Number.isInteger(i) && i >= 0 && i < tranches && actual) actualByTranche[i] = actual;
  }
  return {
    total,
    firstVest: dateOrNull(g.firstVest) ?? reviewDate,
    tranches,
    intervalMonths: Math.max(1, Math.round(Number(g.intervalMonths) || 3)),
    superApplies: g.superApplies !== false,
    keepAfterEnd: g.keepAfterEnd === true, // default: tranches after leaving are forfeited
    actualByTranche,
  };
}

/** A stock code (upper case), e.g. TEAM, AMZN, BRK.B. */
export const TICKER_PATTERN = /^[A-Z][A-Z0-9.-]{0,9}$/;

function positiveOrNull(v) {
  const n = rateOrNull(v);
  return n !== null && n > 0 ? n : null;
}

/** A per-share price value (null when unset / invalid). */
function priceOrNull(value) {
  return readMoney(value, { price: true, positive: true });
}

/**
 * Per-tranche overrides of a share grant: index → { shares?, price? } (`price`
 * a money value; vests always convert at the market exchange rate).
 */
function normaliseTrancheOverrides(raw, tranches) {
  const out = {};
  for (const [k, v] of Object.entries(raw ?? {})) {
    const i = Number(k);
    if (!Number.isInteger(i) || i < 0 || i >= tranches || !v) continue;
    const o = {};
    const shares = Math.round(Number(v.shares));
    if (v.shares !== null && v.shares !== '' && Number.isFinite(shares) && shares >= 0) {
      o.shares = shares;
    }
    const price = priceOrNull(v.price);
    if (price) o.price = price;
    if (Object.keys(o).length) out[i] = o;
  }
  return out;
}

/**
 * A new SHARE grant (RSUs) awarded at a review: `totalShares` of `ticker`
 * vesting in `tranches` every `intervalMonths` from `firstVest` (counts
 * split-adjusted, like the price data). `grantPrice` (optional) is the
 * price the grant was sized at and `growthRate` the employer's assumed yearly
 * growth — together the "intended" vest value. `sellToCover` (null = off) is
 * the fraction of each vest sold on the day to pay the tax. Per-tranche
 * overrides set a tranche's share count (the rest split the remainder) or its
 * vest price / exchange rate (else market data).
 */
function normaliseShareGrant(g, reviewDate) {
  if (!g) return null;
  const ticker = strOrNull(g.ticker)?.toUpperCase() ?? null;
  const totalShares = wholeAtLeast(g.totalShares, 0);
  if (!ticker || !TICKER_PATTERN.test(ticker) || !totalShares) return null;
  const tranches = wholeAtLeast(g.tranches ?? 16, 1);
  const stc = positiveOrNull(g.sellToCover);
  return {
    ticker,
    totalShares,
    firstVest: dateOrNull(g.firstVest) ?? reviewDate,
    tranches,
    intervalMonths: wholeAtLeast(g.intervalMonths ?? 3, 1),
    grantPrice: priceOrNull(g.grantPrice),
    growthRate: rateOrNull(g.growthRate) ?? 0,
    keepAfterEnd: g.keepAfterEnd === true,
    sellToCover: stc === null ? null : Math.min(1, stc),
    trancheOverrides: normaliseTrancheOverrides(g.trancheOverrides, tranches),
  };
}

/**
 * The growth rate to default a new share grant to: that of the latest share
 * grant awarded on/before `date` among `events` (one employment's), else 0.
 */
export function previousShareGrowthRate(events, date) {
  let found = null;
  for (const e of events) {
    if (e.kind !== 'review' || !e.shareGrant || e.date > date) continue;
    if (!found || e.date >= found.date) found = e;
  }
  return found ? found.shareGrant.growthRate : 0;
}

/** A compensation event's stored shape (fields depend on `kind`). */
export function normaliseCompEvent(ev, employmentRef) {
  const kind = COMP_EVENT_KINDS.includes(ev?.kind) ? ev.kind : 'one-off';
  const date = dateOrNull(ev?.date) ?? isoDate(new Date());
  const out = {
    kind,
    employmentRef: employmentRef ?? ev?.employmentRef ?? null,
    date,
    label: strOrNull(ev?.label),
  };
  if (kind === 'review') {
    out.base = readMoney(ev.base, { currencies: AUD_ONLY, min: 0 });
    out.super = normaliseSuper(ev.super);
    out.sacrifice = normaliseSacrifice(ev.sacrifice);
    out.bonusTarget = normaliseBonusTarget(ev.bonusTarget);
    out.grant = normaliseGrant(ev.grant, date);
    out.shareGrant = normaliseShareGrant(ev.shareGrant, date);
  } else if (kind === 'leave') {
    const endDate = dateOrNull(ev.endDate);
    out.endDate = endDate && endDate >= date ? endDate : date;
    out.vestingDelayPeriods = wholeAtLeast(ev.vestingDelayPeriods, 0);
  } else {
    out.value = readMoney(ev.value, { min: 0 }) ?? money(0);
    out.superApplies = ev.superApplies === true;
    out.payPeriods = wholeAtLeast(ev.payPeriods, 1);
  }
  return out;
}

function wholeAtLeast(v, min) {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n >= min ? n : min;
}

/**
 * An employment's unpaid leave periods, in date order:
 * `[{ start, end, vestingDelayPeriods, itemId }]` (dates inclusive).
 */
export function leavePeriods(events) {
  return events
    .filter((e) => e.kind === 'leave')
    .map((e) => ({
      start: e.date,
      end: e.endDate,
      vestingDelayPeriods: e.vestingDelayPeriods ?? 0,
      itemId: e.itemId ?? null,
    }))
    .sort((a, b) => a.start.localeCompare(b.start));
}

/** Whether `day` falls inside any of the leave periods. */
export function onLeave(leaves, day) {
  return leaves.some((l) => day >= l.start && day <= l.end);
}

/** A stored percent-or-amount part → its derived plain shape ({ kind, value: rate | AUD cents }). */
function plainPart(part) {
  return { kind: part.kind, value: partValue(part) };
}

/**
 * Carry reviews forward: the full package in effect from each review's date,
 * in date order — `{ date, itemId, annualCents, superRate (null = SG),
 * sacrifice (null = none), bonusTarget (null = none) }`. Derived, so amounts
 * are plain AUD cents (a sacrifice / bonus target `value` is a rate or cents).
 */
export function resolveReviews(events) {
  const reviews = events
    .filter((e) => e.kind === 'review')
    .sort((a, b) => a.date.localeCompare(b.date));
  let annualCents = 0;
  let superRate = null;
  let sacrifice = null;
  let bonusTarget = null;
  return reviews.map((r) => {
    if (r.base) annualCents = r.base.amount;
    if (r.super) superRate = r.super.mode === 'sg' ? null : r.super.rate;
    if (r.sacrifice) sacrifice = r.sacrifice.kind === 'none' ? null : plainPart(r.sacrifice);
    if (r.bonusTarget)
      bonusTarget = bonusTargetActive(r.bonusTarget) ? plainPart(r.bonusTarget) : null;
    return {
      date: r.date,
      itemId: r.itemId ?? null,
      annualCents,
      superRate,
      sacrifice,
      bonusTarget,
    };
  });
}

/** The resolved package in effect on `date` (latest review ≤ date), or null. */
export function packageOn(resolved, date) {
  let found = null;
  for (const p of resolved) {
    if (p.date > date) break;
    found = p;
  }
  return found;
}

function normaliseCoverPeriods(list) {
  return (Array.isArray(list) ? list : [])
    .map((p) => ({ from: dateOrNull(p?.from), to: dateOrNull(p?.to) }))
    .filter((p) => p.from && (!p.to || p.to >= p.from))
    .sort((a, b) => a.from.localeCompare(b.from));
}

/** An AUD-only per-FY figure (null when unset). */
function audFigure(f, name, { min = null } = {}) {
  return readMoney(f?.[name], { currencies: AUD_ONLY, min });
}

function normaliseFyFigures(f) {
  return {
    spouse: f?.spouse === true,
    spouseMlsIncome: audFigure(f, 'spouseMlsIncome', { min: 0 }) ?? money(0),
    dependentChildren: Math.max(0, Math.round(Number(f?.dependentChildren) || 0)),
    // Total super balance at 30 June at the END of this FY (drives next FY's caps).
    tsbEnd: audFigure(f, 'tsbEnd'),
    // Replace the derived concessional total (e.g. with the myGov figure).
    concessionalOverride: audFigure(f, 'concessionalOverride'),
    personalDeductible: audFigure(f, 'personalDeductible', { min: 0 }) ?? money(0),
    nonConcessional: audFigure(f, 'nonConcessional', { min: 0 }) ?? money(0),
  };
}

/**
 * An FY's figures as plain AUD cents for the calculators (`…Cents`; null =
 * unset for `tsbEndCents` / `concessionalOverrideCents`).
 */
export function fyAmounts(settings, fy) {
  const f = fyFigures(settings, fy);
  return {
    spouse: f.spouse,
    spouseMlsIncomeCents: f.spouseMlsIncome.amount,
    dependentChildren: f.dependentChildren,
    tsbEndCents: f.tsbEnd?.amount ?? null,
    concessionalOverrideCents: f.concessionalOverride?.amount ?? null,
    personalDeductibleCents: f.personalDeductible.amount,
    nonConcessionalCents: f.nonConcessional.amount,
  };
}

/** The income settings row's shape (all defaults applied). */
export function normaliseIncomeSettings(s) {
  const p = s?.profile ?? {};
  const byFy = {};
  for (const [k, v] of Object.entries(s?.byFy ?? {})) {
    if (/^\d{4}$/.test(k)) byFy[k] = normaliseFyFigures(v);
  }
  return {
    profile: {
      tfn: p.tfn !== false,
      foreignResident: p.foreignResident === true,
      medicareExemption: MEDICARE_EXEMPTIONS.includes(p.medicareExemption)
        ? p.medicareExemption
        : 'none',
      studyLoan: p.studyLoan === true,
    },
    hospitalCover: normaliseCoverPeriods(s?.hospitalCover),
    byFy,
  };
}

/** The per-FY figures for an FY (defaults when none saved). */
export function fyFigures(settings, fy) {
  return settings.byFy[String(fy)] ?? normaliseFyFigures({});
}

// ─── IDs ─────────────────────────────────────────────────────────────────────

export function employmentUuidOf(employmentItemId) {
  const s = String(employmentItemId);
  return s.startsWith(EMPLOYMENT_PREFIX) ? s.slice(EMPLOYMENT_PREFIX.length) : s;
}

export function compEventItemId(employmentItemId, eventUuid) {
  return `${COMP_EVENT_PREFIX}${employmentUuidOf(employmentItemId)}:${eventUuid}`;
}

// ─── Storage ─────────────────────────────────────────────────────────────────

const withId = (row, normalised) => ({
  itemId: row.itemId,
  createdAt: row.createdAt,
  ...normalised,
});

export async function listEmployments(doc, userId) {
  const rows = await financeStore.listItems(doc, userId, EMPLOYMENT_PREFIX);
  return rows
    .map((r) => withId(r, normaliseEmployment(r)))
    .sort((a, b) => b.startDate.localeCompare(a.startDate));
}

export async function createEmployment(doc, userId, employment, genId = randomUuid) {
  const stored = normaliseEmployment(employment);
  const row = await financeStore.addItem(
    doc,
    userId,
    EMPLOYMENT_PREFIX,
    stored,
    `${EMPLOYMENT_PREFIX}${genId()}`,
  );
  return withId(row, stored);
}

export async function updateEmployment(doc, userId, employmentItemId, employment) {
  const stored = normaliseEmployment(employment);
  await financeStore.updateItem(doc, userId, employmentItemId, stored);
  return { itemId: employmentItemId, ...stored };
}

/** Delete an employment and ALL its comp events; returns the events removed. */
export async function deleteEmployment(doc, userId, employmentItemId) {
  const prefix = `${COMP_EVENT_PREFIX}${employmentUuidOf(employmentItemId)}:`;
  const events = await financeStore.listItems(doc, userId, prefix);
  await Promise.all(events.map((e) => financeStore.deleteItem(doc, userId, e.itemId)));
  await financeStore.deleteItem(doc, userId, employmentItemId);
  return events.length;
}

/** Every comp event (all employments), date order. */
export async function listCompEvents(doc, userId) {
  const rows = await financeStore.listItems(doc, userId, COMP_EVENT_PREFIX);
  return rows
    .map((r) => withId(r, normaliseCompEvent(r)))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export async function createCompEvent(doc, userId, employmentItemId, event, genId = randomUuid) {
  const stored = normaliseCompEvent(event, employmentItemId);
  const itemId = compEventItemId(employmentItemId, genId());
  const row = await financeStore.addItem(doc, userId, COMP_EVENT_PREFIX, stored, itemId);
  return withId(row, stored);
}

export async function updateCompEvent(doc, userId, eventItemId, event) {
  const stored = normaliseCompEvent(event);
  await financeStore.updateItem(doc, userId, eventItemId, stored);
  return { itemId: eventItemId, ...stored };
}

export function deleteCompEvent(doc, userId, eventItemId) {
  return financeStore.deleteItem(doc, userId, eventItemId);
}

export async function getIncomeSettings(doc, userId) {
  const row = await financeStore.getItem(doc, userId, INCOME_SETTINGS_ID);
  return normaliseIncomeSettings(row ?? {});
}

export async function saveIncomeSettings(doc, userId, settings) {
  const stored = normaliseIncomeSettings(settings);
  await financeStore.updateItem(doc, userId, INCOME_SETTINGS_ID, stored);
  return stored;
}
